import React, { useState, useRef, useCallback, useEffect } from 'react'

import Tree from '~co/virtual/tree'
import Item from '../item'
import Group from '../group'
import Empty from './empty'
import { Item as UIItem } from '~ui'

const EMPTY_CUSTOM_ROWS = []

//measures a row's rendered height once, then renders children(height)
function ItemHeightCallback({ children }) {
  const [height, setHeight] = React.useState(0)
  const bindRef = React.useCallback(
    (el) => {
      if (el && !height) setHeight(el.offsetHeight)
    },
    [height],
  )
  if (!height) return <UIItem ref={bindRef} />
  return children(height)
}

export default function CollectionsTree({
    data,
    activeId,
    blankId,
    customRows = EMPTY_CUSTOM_ROWS,
    customRowRenderer,
    events,
    actions
}) {
    const [dataCheckpoint, setDataCheckpoint] = useState(0)
    const [scrollToIndex, setScrollToIndex] = useState(-1)

    const latest = useRef({ data, activeId, blankId, customRows, customRowRenderer, events, actions })
    latest.current = { data, activeId, blankId, customRows, customRowRenderer, events, actions }

    const scrollToId = useCallback(id =>
        setTimeout(() => {
            const { data } = latest.current
            setScrollToIndex(
                data.findIndex(({ item }) => item && item._id == id)
            )
        }),
        []
    )

    const prev = useRef({ activeId, blankId, data, customRows })
    useEffect(() => {
        const p = prev.current
        prev.current = { activeId, blankId, data, customRows }

        //scroll to active
        if (p.activeId != activeId &&
            typeof activeId != 'object' &&
            data.length)
            scrollToId(activeId)

        if (p.data != data ||
            p.customRows != customRows)
            setDataCheckpoint(c => c + 1)

        //scroll to blank new collection
        if (p.blankId != blankId)
            scrollToId(blankId)
    }, [activeId, blankId, data, customRows, scrollToId])

    //mount scroll
    useEffect(() => {
        if (typeof latest.current.activeId != 'object')
            scrollToId(latest.current.activeId)
    }, [scrollToId])

    //rendering rows
    const rowRenderer = useCallback((index, provided, snapshot = {}) => {
        const { isDragging, combineTargetFor } = snapshot
        const { data, activeId, customRows, customRowRenderer, events, actions } = latest.current
        const row = data[index]

        if (!row)
            if (customRowRenderer)
                return customRowRenderer(customRows[index - data.length])

        let Component, active = false, multiselect = false
        switch (row.type) {
            case 'group':
                Component = Group
                break

            case 'collection':
                Component = Item

                switch (typeof activeId) {
                    case 'object':
                        active = activeId.includes(row.item._id)
                        multiselect = true
                        break

                    default:
                        active = activeId == row.item._id
                        break
                }
                break

            default:
                return null
        }

        return (
            <Component
                //item,etc
                {...row}
                //tree specififc
                active={active}
                multiselect={multiselect}
                events={events}
                actions={actions}
                //drag/drop specific
                isDragging={isDragging}
                isDropping={combineTargetFor ? true : false}
            />
        )
    }, [])

    //drag/drop
    const rowIsDraggable = useCallback(index => {
        //disable when multiselect
        if (typeof latest.current.activeId == 'object')
            return false

        const { data } = latest.current
        const row = data[index]

        //disable for system collections
        if (row)
            switch (row.type) {
                case 'collection':
                    if (row.item._id <= 0) {
                        if (row.item._id == -101)
                            return true
                        else
                            return false
                    }
                    return true

                case 'group':
                    return row.system ? false : true
            }

        return false
    }, [])

    const rowIsDroppable = useCallback((from, to) => {
        const { data } = latest.current
        const origin = data[from]
        const target = data[to]

        if (origin && origin.item)
            switch (origin.type) {
                case 'collection':
                    if (!origin.item.access || !origin.item.access.draggable)
                        return false

                    if (target.item)
                        return target.item.access && (target.item.access.root || target.item.access.draggable)

                    return true
            }

        return false
    }, [])

    const onDragStart = useCallback(index => {
        const { data, actions } = latest.current
        const row = data[index]

        if (row)
            switch (row.type) {
                case 'collection': {
                    const { item } = row

                    if (item.expanded)
                        actions.oneToggle(item._id)
                } break
            }
    }, [])

    const onDragEnd = useCallback(async (from, to, action) => {
        const { data, actions } = latest.current
        const origin = data[from]
        const target = data[to]

        if (!origin || !target) return

        switch (origin.type) {
            case 'collection': {
                if (action == 'move')
                    if (target.type == 'collection') {
                        if (to >= from)
                            actions.oneReorder(origin.item._id, { after: target.item._id })
                        else if (to <= from)
                            actions.oneReorder(origin.item._id, { before: target.item._id })
                    }
                    else {
                        //to end of previous group
                        if (to < from &&
                            data[to - 1]) {
                            const p = data[to - 1]

                            actions.oneReorder(
                                origin.item._id,
                                p.item ?
                                    { after: data[to - 1].item._id } :
                                    { to: p._id }
                            )
                        }
                        //to start of current group
                        else
                            action = 'combine'
                    }

                if (action == 'combine')
                    actions.oneReorder(origin.item._id, { to: target.item ? target.item._id : target._id })
            } break

            case 'group': {
                let after, before

                if (to > from) {
                    if (target.type == 'group')
                        after = target._id
                    else
                        for (let i = to - 1; i > 0; i--)
                            if (data[i].type == 'group') {
                                after = data[i]._id
                                break
                            }
                } else if (to < from) {
                    if (target.type == 'group')
                        before = target._id
                    else
                        for (let i = to + 1; i < data.length; i++)
                            if (data[i].type == 'group') {
                                before = data[i]._id
                                break
                            }
                }

                if (after || before)
                    actions.groupReorder(origin._id, { after, before })
            } break
        }
    }, [])

    if (!data.length)
        return <Empty />

    return (
        <ItemHeightCallback>{itemHeight =>
            <Tree
                className={'ui-tree'}

                //base
                item={rowRenderer}
                totalCount={data.length + customRows.length}
                dataKey={String(activeId) + (typeof activeId) + dataCheckpoint} //only used to re-render when data re-ordered from outside
                scrollToIndex={scrollToIndex}
                itemHeight={itemHeight}

                //custom
                rowIsDraggable={rowIsDraggable}
                rowIsDroppable={rowIsDroppable}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
            />
        }</ItemHeightCallback>
    )
}
